import { Alert, Descriptions, Tag } from 'antd';
import { OperatorForm } from './OperatorForm';
import { GenealogyPreviewChart } from './GenealogyPreviewChart';
import { AssetImpactSummary } from './AssetImpactSummary';
import type { GenealogyPreview } from './GenealogyPreviewChart';

export type DeleteUserPreview = {
  user_id: number;
  username: string;
  is_sub_account: boolean;
  delete_sub_accounts: boolean;
  users_to_delete: Array<{ id: number; username: string; is_sub: boolean }>;
  referral_downline_count: number;
  binary_children_count: number;
  sub_account_count: number;
  referral_children_reparented: Array<{ id: number; username: string; is_sub: boolean }>;
  binary_children_reparented: Array<{ id: number; username: string; position: string | null }>;
  current_referrer: { id: number; username: string } | null;
  current_parent: { id: number; username: string } | null;
  tree_repair: {
    referral_reparent_to: { id: number; username: string } | null;
    binary_children_move_to: { id: number; username: string } | null;
  };
  assets_removed: {
    rows: Array<{
      id: number;
      username: string;
      is_sub: boolean;
      assets: Record<string, string>;
      orb_wallet_display: string | null;
    }>;
    totals: Record<string, string>;
    orb_wallet_total: string;
  };
  genealogy: GenealogyPreview;
  note: string;
  would_apply: boolean;
};

export function DeleteUserForm({
  userId,
  username,
  onDone,
}: {
  userId: number;
  username: string;
  onDone?: () => void;
}) {
  return (
    <OperatorForm<DeleteUserPreview>
      endpoint={`/api/admin/users/${userId}/delete`}
      modalWidth={920}
      fields={[
        {
          name: 'confirm_username',
          label: '确认用户名（须与待删账户完全一致）',
          type: 'text',
          required: true,
          placeholder: username,
        },
        {
          name: 'delete_sub_accounts',
          label: '一并删除子账户',
          type: 'select',
          required: true,
          initialValue: 1,
          options: [
            { value: 1, label: '是 — 删除主号时连同旗下子账户' },
            { value: 0, label: '否 — 仅删除当前账户' },
          ],
        },
        {
          name: 'note',
          label: '操作备注',
          type: 'textarea',
          required: true,
          placeholder: `删除 ${username} 的原因…`,
        },
      ]}
      submitLabel="预览删除影响"
      renderPreview={(p) => (
        <div style={{ maxHeight: '70vh', overflow: 'auto' }}>
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 12 }}
            message="仅删除目标主/子账户；直接推荐下级与二叉直接下级会改挂， deeper 网体成员不在删除范围。"
          />
          <GenealogyPreviewChart data={p.genealogy} />
          <Descriptions column={1} size="small" bordered>
            <Descriptions.Item label="待删账户">
              #{p.user_id} {p.username}
              {p.is_sub_account && <Tag style={{ marginLeft: 8 }}>子账户</Tag>}
            </Descriptions.Item>
            <Descriptions.Item label="将删除">
              {p.users_to_delete.map((u) => (
                <div key={u.id}>
                  #{u.id} {u.username}
                  {u.is_sub ? '（子）' : '（主）'}
                </div>
              ))}
            </Descriptions.Item>
            <Descriptions.Item label="推荐直接下级改挂">
              {p.referral_children_reparented.length === 0
                ? '无'
                : p.referral_children_reparented.map((u) => (
                    <div key={u.id}>
                      #{u.id} {u.username}
                      {u.is_sub ? '（子）' : ''} →{' '}
                      {p.tree_repair.referral_reparent_to
                        ? `#${p.tree_repair.referral_reparent_to.id} ${p.tree_repair.referral_reparent_to.username}`
                        : '根'}
                    </div>
                  ))}
            </Descriptions.Item>
            <Descriptions.Item label="二叉直接下级改挂">
              {p.binary_children_reparented.length === 0
                ? '无'
                : p.binary_children_reparented.map((u) => (
                    <div key={u.id}>
                      #{u.id} {u.username} [{u.position ?? '—'}] →{' '}
                      {p.tree_repair.binary_children_move_to
                        ? `#${p.tree_repair.binary_children_move_to.id} ${p.tree_repair.binary_children_move_to.username}`
                        : '根'}
                    </div>
                  ))}
            </Descriptions.Item>
            <Descriptions.Item label="备注">{p.note}</Descriptions.Item>
          </Descriptions>
          <AssetImpactSummary
            title="将从平台移除的资产"
            rows={p.assets_removed.rows}
            totals={p.assets_removed.totals}
            orbWalletTotal={p.assets_removed.orb_wallet_total}
          />
        </div>
      )}
      onDone={onDone}
    />
  );
}
